import { Effect, Match, Scope, Semaphore } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';

import { applyChange, type ListingState } from '../../capability/productListing/listingSelection';
import type { ListingScopeKey } from '../../capability/productListing/listingScope';
import { awaitPrefetch, makeListingPrefetch, type ListingIntent } from './ListingPrefetch';
import type { resolveListingCacheOptions } from './ListingCacheOptions';
import type { makeListingQueries } from './makeListingQueries';

/** Owns speculative-read permits, scopes, candidate queries, and intent activation. */
export const makeListingPrefetchAtoms = <ER>(deps: {
  readonly state: (scopeKey: ListingScopeKey) => Atom.Atom<ListingState>;
  readonly queries: Pick<
    ReturnType<typeof makeListingQueries<ER>>,
    'epoch' | 'products' | 'facets'
  >;
  readonly itemsPerPage: number;
  readonly settings: Pick<
    ReturnType<typeof resolveListingCacheOptions>,
    'prefetchConcurrency' | 'prefetchDelay' | 'prefetchEnabled'
  >;
}) => {
  const settings = deps.settings;
  const prefetchBudget = Atom.make(
    Effect.gen(function* () {
      return {
        budget: yield* Semaphore.make(settings.prefetchConcurrency),
        scope: yield* Scope.Scope,
      };
    })
  ).pipe(Atom.keepAlive);
  const prefetchController = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => {
      get(deps.state(scopeKey));
      const epoch = get(deps.queries.epoch);
      return Effect.gen(function* () {
        const session = yield* get.result(prefetchBudget);
        return yield* makeListingPrefetch({
          budget: session.budget,
          workScope: session.scope,
          delay: settings.prefetchDelay,
          enabled: () =>
            Match.value<unknown>(globalThis).pipe(
              Match.when({ navigator: { connection: { saveData: true } } }, () => false),
              Match.orElse(() => settings.prefetchEnabled)
            ),
          prefetch: (change) =>
            Effect.suspend(() => {
              const candidate = applyChange(get.registry.get(deps.state(scopeKey)), change);
              return Effect.all(
                [
                  awaitPrefetch(
                    get.registry,
                    deps.queries.products({ ...candidate, itemsPerPage: deps.itemsPerPage }, epoch)
                      .current
                  ),
                  awaitPrefetch(get.registry, deps.queries.facets(candidate, epoch).current),
                ],
                { concurrency: 'unbounded' }
              ).pipe(Effect.asVoid);
            }),
        });
      });
    })
  );
  const intentFn = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.fn((command: ListingIntent, get) =>
      get
        .result(prefetchController(scopeKey))
        .pipe(Effect.flatMap((controller) => controller.intent(command)))
    )
  );
  const prefetch = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable(
      (get) => {
        get(prefetchController(scopeKey));
        return undefined;
      },
      (context, command: ListingIntent) => {
        context.set(intentFn(scopeKey), command);
        context.get(intentFn(scopeKey));
      }
    )
  );
  return prefetch;
};
